import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { CheckCircle2, AlertTriangle, Users, Loader2, CalendarDays, MapPin, Clock } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function PublicRoster() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/api/public/roster/${token}`);
        if (!res.ok) {
          const j = await res.json().catch(() => ({}));
          throw new Error(j.detail || 'This live roster link is invalid or has expired.');
        }
        setData(await res.json());
      } catch (e) {
        setError(e.message || 'Unable to load roster.');
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center" data-testid="public-roster-loading">
        <Loader2 className="w-8 h-8 animate-spin text-cyan-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6" data-testid="public-roster-error">
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-8 max-w-md text-center">
          <AlertTriangle className="w-10 h-10 text-amber-500 mx-auto mb-3" />
          <h1 className="text-lg font-semibold text-slate-900 mb-1">Roster unavailable</h1>
          <p className="text-sm text-slate-500">{error}</p>
        </div>
      </div>
    );
  }

  const { event, summary, roster } = data;
  const fully = summary.fully_staffed;

  return (
    <div className="min-h-screen bg-slate-50 py-8 px-4" data-testid="public-roster-page">
      <div className="max-w-2xl mx-auto">
        <div className="flex items-center gap-2 mb-5">
          <span className="text-cyan-600 font-bold text-lg">iEvntür</span>
          <span className="text-slate-300">·</span>
          <span className="text-slate-500 text-sm">Live event roster</span>
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
          {/* Event header */}
          <div className="p-6 border-b border-slate-100">
            <h1 className="text-xl font-bold text-slate-900" data-testid="roster-event-name">{event.name}</h1>
            <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-sm text-slate-500">
              {event.date && <span className="inline-flex items-center gap-1"><CalendarDays className="w-4 h-4" />{event.date}</span>}
              {(event.start_time || event.end_time) && (
                <span className="inline-flex items-center gap-1"><Clock className="w-4 h-4" />{(event.start_time || '').slice(0,5)}{event.end_time ? `–${event.end_time.slice(0,5)}` : ''}</span>
              )}
              {(event.city || event.state) && (
                <span className="inline-flex items-center gap-1"><MapPin className="w-4 h-4" />{[event.city, event.state].filter(Boolean).join(', ')}</span>
              )}
            </div>
          </div>

          {/* Status banner */}
          <div className={`px-6 py-4 flex items-center gap-3 ${fully ? 'bg-green-50' : 'bg-amber-50'}`} data-testid="roster-status-banner">
            {fully ? <CheckCircle2 className="w-6 h-6 text-green-600 shrink-0" /> : <AlertTriangle className="w-6 h-6 text-amber-600 shrink-0" />}
            <div>
              <div className={`font-semibold ${fully ? 'text-green-800' : 'text-amber-800'}`}>{summary.status_label}</div>
              <div className={`text-sm ${fully ? 'text-green-700' : 'text-amber-700'}`}>
                {fully
                  ? (summary.all_confirmed ? 'All positions filled and confirmed.' : `All positions filled — ${summary.awaiting_confirmation} awaiting confirmation.`)
                  : `${summary.filled} of ${summary.requested} filled — ${summary.open} still open. We are actively seeking additional staff.`}
              </div>
            </div>
          </div>

          {/* Counts */}
          <div className="grid grid-cols-4 divide-x divide-slate-100 border-b border-slate-100 text-center">
            {[
              ['Requested', summary.requested],
              ['Filled', summary.filled],
              ['Confirmed', summary.confirmed],
              ['Open', summary.open],
            ].map(([label, val]) => (
              <div key={label} className="py-4">
                <div className="text-2xl font-bold text-slate-900">{val}</div>
                <div className="text-[11px] uppercase tracking-wide text-slate-400">{label}</div>
              </div>
            ))}
          </div>

          {/* Roster */}
          <div className="p-6">
            <div className="flex items-center gap-2 mb-3 text-slate-700 font-semibold text-sm">
              <Users className="w-4 h-4" /> Roster
            </div>
            {roster.length === 0 ? (
              <p className="text-sm text-slate-500">No talent booked yet — we're actively sourcing staff for your event.</p>
            ) : (
              <ul className="divide-y divide-slate-100" data-testid="roster-list">
                {roster.map((r, i) => (
                  <li key={i} className="py-3 flex items-center justify-between" data-testid={`roster-row-${i}`}>
                    <div>
                      <span className="font-medium text-slate-900">{r.first_name}</span>
                      <span className="text-slate-400 text-sm"> · {r.role}{r.kind === 'Backup' ? ' (Backup)' : ''}</span>
                    </div>
                    <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${r.status === 'Confirmed' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                      {r.status}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        <p className="text-center text-xs text-slate-400 mt-5">
          Live staffing status from iEvntür — no login required. Questions? support@ievntur.com
        </p>
      </div>
    </div>
  );
}
